// src/pages/products/ViewProducts.tsx
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { FiPlus, FiSearch } from "react-icons/fi";

import AdminLayout from "@/layouts/AdminLayout";
import { useProductsPage } from "./hooks/useProductsPage";

import ProductsTable from "@/components/products/ProductsTable";
import { CreateProductForm } from "@/components/products/forms/CreateProductForm";
import { EditProductForm } from "@/components/products/forms/EditProductForm";

/* ======================================================================
 * Breadcrumbs
 * ====================================================================== */

function Breadcrumbs() {
  const { pathname } = useLocation();
  const parts = pathname.split("/").filter(Boolean);

  const crumbs = parts.map((p, idx) => {
    const href = "/" + parts.slice(0, idx + 1).join("/");
    const label = p
      .split("-")
      .map((s) => (s[0] ? s[0].toUpperCase() + s.slice(1) : s))
      .join(" ");
    return { href, label };
  });

  return (
    <nav className="text-xs text-gray-500">
      <ol className="flex flex-wrap items-center gap-1">
        <li>
          <Link to="/" className="hover:text-gray-700">
            Home
          </Link>
        </li>
        {crumbs.map((c, i) => (
          <li key={c.href} className="flex items-center gap-1">
            <span>/</span>
            {i < crumbs.length - 1 ? (
              <Link to={c.href} className="hover:text-gray-700">
                {c.label}
              </Link>
            ) : (
              <span className="text-gray-700">{c.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/* ======================================================================
 * Modal shell
 * ====================================================================== */

type ModalProps = {
  title: string;
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
};

const Modal: React.FC<ModalProps> = ({
  title,
  open,
  onClose,
  children,
}) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute inset-0 grid place-items-center p-4">
        <div className="w-full max-w-2xl rounded-2xl border border-gray-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">
              {title}
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="max-h-[70vh] overflow-y-auto p-4 sm:p-6">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ======================================================================
 * Confirm dialog
 * ====================================================================== */

type ConfirmDialogProps = {
  open: boolean;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
};

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title = "Confirm",
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/30" onClick={onCancel} />
      <div className="absolute inset-0 grid place-items-center p-4">
        <div className="w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">
              {title}
            </h3>
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="p-5 text-sm text-gray-700">{message}</div>
          <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-4 py-3">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              {cancelText}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700"
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ======================================================================
 * Main page
 * ====================================================================== */

const ViewProducts: React.FC = () => {
  const {
    // filters / pagination
    search,
    setSearch,
    filters,
    setFilters,
    page,
    setPage,
    pageSize,

    // list data
    rows,
    total,
    isLoading,
    isFetching,

    // modals
    createOpen,
    setCreateOpen,
    editingId,

    // delete confirm
    confirmDelete,
    setConfirmDelete,

    // edit detail
    editingValue,
    isDetailLoading,

    // mutation flags / errors
    creating,
    updating,
    deleting,
    createError,
    updateError,

    // handlers
    handleOpenEdit,
    handleCloseEdit,
    handleAskDelete,
    handleConfirmDelete,
    handleCreate,
    handleUpdate,
  } = useProductsPage();

  const effectiveLoading = isLoading || isFetching;

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            Products
          </h1>
          <div className="mt-1">
            <Breadcrumbs />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative">
            <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search products…"
              className="w-56 rounded-lg border border-gray-300 bg-white pl-10 pr-3 py-2 text-sm outline-none placeholder:text-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
            />
          </div>

          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700"
          >
            <FiPlus />
            Add Product
          </button>
        </div>
      </div>

      {/* Info strip on refetch */}
      {isFetching && !isLoading && (
        <div className="mb-3 rounded-lg border border-sky-100 bg-sky-50 px-3 py-2 text-xs text-sky-700">
          Refreshing list…
        </div>
      )}

      {/* Table */}
      <ProductsTable
        rows={rows}
        page={page}
        pageSize={pageSize}
        total={total}
        isLoading={effectiveLoading}
        filters={filters}
        setFilters={setFilters}
        onPageChange={setPage}
        onEdit={handleOpenEdit}
        onDelete={handleAskDelete}
      />

      {/* CREATE modal */}
      <Modal
        title="Add Product"
        open={createOpen}
        onClose={() => {
          if (!creating) setCreateOpen(false);
        }}
      >
        <CreateProductForm
          onSubmit={handleCreate}
          onCancel={
            creating ? undefined : () => setCreateOpen(false)
          }
          isSubmitting={creating}
          apiError={createError}
        />
      </Modal>

      {/* EDIT modal */}
      <Modal
        title="Edit Product"
        open={editingId !== null}
        onClose={() => {
          if (!updating) handleCloseEdit();
        }}
      >
        {isDetailLoading && (
          <div className="py-6 text-center text-sm text-gray-500">
            Loading product…
          </div>
        )}

        {!isDetailLoading && editingValue && (
          <EditProductForm
            value={editingValue}
            onSubmit={handleUpdate}
            onCancel={updating ? undefined : handleCloseEdit}
            isSubmitting={updating}
            apiError={updateError}
          />
        )}
      </Modal>

      {/* DELETE confirm */}
      <ConfirmDialog
        open={confirmDelete.open}
        title="Delete product"
        message={
          confirmDelete.row
            ? `Delete product "${confirmDelete.row.name}"?`
            : "Delete this product?"
        }
        confirmText={deleting ? "Deleting…" : "Delete"}
        cancelText="Cancel"
        onCancel={() => {
          if (!deleting) {
            setConfirmDelete({ open: false, row: null });
          }
        }}
        onConfirm={handleConfirmDelete}
      />
    </AdminLayout>
  );
};

export default ViewProducts;
